import { Tag } from "antd";
import type {
	AutoApprovalThreshold,
	EnvironmentClassification,
	EnvironmentProvider,
	EnvironmentStatus,
} from "@/common/artemis-sdk/graphql";
import {
	CLASSIFICATION_COLORS,
	createLabelTag,
	createStatusBadge,
	type TagStatus,
} from "../status-pill";

// Status — uses Badge (dot + text) since it represents a live state
export const EnvironmentStatusColorMap: Record<EnvironmentStatus, TagStatus> = {
	ACTIVE: "success",
	DISABLED: "default",
	FAILED: "error",
};

export const EnvironmentStatusLabelMap: Record<EnvironmentStatus, string> = {
	ACTIVE: "Active",
	DISABLED: "Disabled",
	FAILED: "Failed",
};

export const EnvironmentStatusBadge = createStatusBadge(
	EnvironmentStatusColorMap,
	EnvironmentStatusLabelMap,
);

// Auto-approval threshold — automation-policy tier, not a status; plain tag, no color.
const AutoApprovalThresholdMap: Record<AutoApprovalThreshold, string> = {
	HIGH: "Autonomous",
	MEDIUM: "Supervised",
	NONE: "Human-gated",
};
export const AutoApprovalThresholdBadge = createLabelTag(
	AutoApprovalThresholdMap,
);

// Classification — Tag, production is visually distinct from non-production (a
// classification label, not a danger state, so no red)
export const EnvironmentClassificationMap: Record<
	EnvironmentClassification,
	{ color: string; label: string }
> = {
	PRODUCTION: { color: CLASSIFICATION_COLORS.brandStrong, label: "Production" },
	NON_PRODUCTION: {
		color: CLASSIFICATION_COLORS.neutral,
		label: "Non-production",
	},
};
export function EnvironmentClassificationBadge({
	classification,
}: {
	classification: EnvironmentClassification;
}) {
	const { color, label } = EnvironmentClassificationMap[classification];
	return <Tag color={color}>{label}</Tag>;
}

// Provider — plain Tag, no color semantics
export const EnvironmentProviderLabelMap: Record<EnvironmentProvider, string> =
	{
		AWS: "AWS",
		AZURE: "Azure",
		GOOGLE_CLOUD: "Google Cloud",
		CLOUDFLARE: "Cloudflare",
		VERCEL: "Vercel",
		OTHER: "Other",
	};
export const EnvironmentProviderBadge = createLabelTag(
	EnvironmentProviderLabelMap,
);

export const EnvironmentClassificationLabelMap: Record<
	EnvironmentClassification,
	string
> = {
	PRODUCTION: "Production",
	NON_PRODUCTION: "Non-production",
};
